import React, { useState } from 'react';
import {
  useDataProvider,
  useNotify,
  useRefresh,
} from 'react-admin';
import {
  Box,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  FormControlLabel,
  Stack,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  CalendarMonth as CalendarIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Save as SaveIcon,
} from '@mui/icons-material';

interface DriverData {
  id: number;
  employmentHistory?: any[];
}

interface DriverEmploymentTabProps {
  driverData: DriverData | null;
  onRefresh?: () => void;
}

// Province/State data
const CANADIAN_PROVINCES = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' }
];

const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' }
];

export const DriverEmploymentTab: React.FC<DriverEmploymentTabProps> = ({ driverData, onRefresh }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  // Safely derived values from possibly-null driverData
  const employmentHistory = driverData?.employmentHistory ?? [];
  const isDriverLoaded = !!driverData?.id;

  // Get province/state options based on selected country
  const getProvinceStateOptions = () => {
    return employmentForm.countryCode === 'CA' ? CANADIAN_PROVINCES : US_STATES;
  };

  // Employment dialog state
  const [employmentDialogOpen, setEmploymentDialogOpen] = useState(false);
  const [editingEmployment, setEditingEmployment] = useState(null);
  const [employmentForm, setEmploymentForm] = useState({
    company: '',
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    current: false,
    address1: '',
    address2: '',
    city: '',
    regionCode: '',
    countryCode: 'CA',
    postalCode: '',
    contactName: '',
    contactTitle: '',
    contactPhone: '',
    contactPhoneExt: '',
    contactEmail: ''
  });

  const handleEditEmployment = (employment: any) => {
    setEditingEmployment(employment);
    setEmploymentForm({
      company: employment.company || '',
      title: employment.title || '',
      description: employment.description || '',
      startDate: employment.startDate ? employment.startDate.split('T')[0] : '',
      endDate: employment.endDate ? employment.endDate.split('T')[0] : '',
      current: employment.current || false,
      address1: employment.address1 || '',
      address2: employment.address2 || '',
      city: employment.city || '',
      regionCode: employment.regionCode || '',
      countryCode: employment.countryCode || 'CA',
      postalCode: employment.postalCode || '',
      contactName: employment.contactName || '',
      contactTitle: employment.contactTitle || '',
      contactPhone: employment.contactPhone || '',
      contactPhoneExt: employment.contactPhoneExt || '',
      contactEmail: employment.contactEmail || ''
    });
    setEmploymentDialogOpen(true);
  };

  const handleSaveEmployment = async () => {
    try {
      // Validate required fields
      if (!employmentForm.company.trim()) {
        notify('Company name is required', { type: 'error' });
        return;
      }
      if (!employmentForm.title.trim()) {
        notify('Job title is required', { type: 'error' });
        return;
      }
      if (!employmentForm.description.trim()) {
        notify('Position description is required', { type: 'error' });
        return;
      }
      if (!employmentForm.startDate) {
        notify('Start date is required', { type: 'error' });
        return;
      }

      // Helper function to convert empty strings to null for optional fields
      const cleanData = (data: any) => {
        const cleaned: any = {};
        Object.keys(data).forEach(key => {
          const value = data[key];
          // Keep required fields and booleans as-is, convert empty strings to null
          if (key === 'driverId' || key === 'current' || (typeof value === 'string' && value.trim() !== '')) {
            cleaned[key] = value;
          } else if (typeof value === 'string' && value.trim() === '') {
            cleaned[key] = null;
          } else {
            cleaned[key] = value;
          }
        });
        return cleaned;
      };

      const employmentData = {
        company: employmentForm.company,
        title: employmentForm.title,
        description: employmentForm.description,
        startDate: employmentForm.startDate,
        endDate: employmentForm.endDate,
        current: employmentForm.current,
        address1: employmentForm.address1,
        address2: employmentForm.address2,
        city: employmentForm.city,
        regionCode: employmentForm.regionCode,
        countryCode: employmentForm.countryCode,
        postalCode: employmentForm.postalCode,
        contactName: employmentForm.contactName,
        contactTitle: employmentForm.contactTitle,
        contactPhone: employmentForm.contactPhone,
        contactPhoneExt: employmentForm.contactPhoneExt,
        contactEmail: employmentForm.contactEmail
      };

      if (editingEmployment) {
        // Update existing employment
        await dataProvider.update('drivers/employment', {
          id: editingEmployment.id,
          data: cleanData(employmentData)
        });
        notify('Employment updated successfully', { type: 'success' });
      } else {
        // Create new employment
        if (!driverData?.id) {
          notify('Driver not loaded yet. Please wait and try again.', { type: 'warning' });
          return;
        }
        await dataProvider.create('drivers/employment', {
          data: cleanData({
            ...employmentData,
            driverId: driverData.id
          })
        });
        notify('Employment added successfully', { type: 'success' });
      }
      setEmploymentDialogOpen(false);
      if (onRefresh) {
        onRefresh();
      } else {
        refresh();
      }
    } catch (error) {
      console.error('Error saving employment:', error);
      notify('Failed to save employment', { type: 'error' });
    }
  };

  const handleDeleteEmployment = async (employmentId: number) => {
    if (window.confirm('Are you sure you want to delete this employment record?')) {
      try {
        await dataProvider.delete('drivers/employment', { id: employmentId });
        notify('Employment record deleted successfully', { type: 'success' });
        if (onRefresh) {
          onRefresh();
        } else {
          refresh();
        }
      } catch (error) {
        console.error('Error deleting employment record:', error);
        notify('Failed to delete employment record', { type: 'error' });
      }
    }
  };

  return (
    <Box>
      {/* Employment Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" gutterBottom>Employment History</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          disabled={!isDriverLoaded}
          onClick={() => {
            setEmploymentForm({
              company: '',
              title: '',
              description: '',
              startDate: '',
              endDate: '',
              current: false,
              address1: '',
              address2: '',
              city: '',
              regionCode: '',
              countryCode: 'CA',
              postalCode: '',
              contactName: '',
              contactTitle: '',
              contactPhone: '',
              contactPhoneExt: '',
              contactEmail: ''
            });
            setEditingEmployment(null);
            setEmploymentDialogOpen(true);
          }}
        >
          Add Employment
        </Button>
      </Box>

      {employmentHistory.length ? (
        <Table size="small" sx={{ mb: 4 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '25%' }}>Company</TableCell>
              <TableCell sx={{ width: '20%' }}>Title</TableCell>
              <TableCell sx={{ width: '20%' }}>Start Date</TableCell>
              <TableCell sx={{ width: '30%' }}>Status</TableCell>
              <TableCell align="right" sx={{ width: '5%' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employmentHistory.map((employment: any, index: number) => (
              <TableRow
                key={index}
                sx={{
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <TableCell>
                  <Typography variant="subtitle2">
                    {employment.company || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {employment.title || 'N/A'}
                </TableCell>
                <TableCell>
                  {employment.startDate ? new Date(employment.startDate).toLocaleDateString() : 'N/A'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={employment.current ? 'CURRENT' : 'PREVIOUS'}
                    color={employment.current ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleEditEmployment(employment)}
                    color="primary"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No employment history on file
        </Typography>
      )}

      {/* Add Employment Dialog */}
      <Dialog
        open={employmentDialogOpen}
        onClose={() => setEmploymentDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: '80vh',
            maxWidth: '1400px'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #e0e0e0',
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WorkIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h5" component="div" fontWeight={600}>
              {editingEmployment ? 'Edit Employment Record' : 'Add Employment Record'}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 5, py: 4 }}>
          <Stack spacing={5}>
            {/* Company Information Section */}
            <Box sx={{
              p: 3,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="h6" gutterBottom sx={{
                mb: 3,
                color: 'primary.main',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <BusinessIcon fontSize="small" />
                Company Information
              </Typography>

              <Grid container spacing={4}>
                <Grid item xs={12} md={2}>
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      Company Name *
                    </Typography>
                    <MuiTextField
                      fullWidth
                      value={employmentForm.company}
                      onChange={(e) => setEmploymentForm({
                        ...employmentForm,
                        company: e.target.value
                      })}
                      placeholder="Enter company name"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: 'white',
                          },
                        }
                      }}
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12} md={2}>
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      Job Title *
                    </Typography>
                    <MuiTextField
                      fullWidth
                      required
                      value={employmentForm.title}
                      onChange={(e) => setEmploymentForm({
                        ...employmentForm,
                        title: e.target.value
                      })}
                      placeholder="Enter job title"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: 'white',
                          },
                        }
                      }}
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      Position Description *
                    </Typography>
                    <MuiTextField
                      fullWidth
                      required
                      multiline
                      rows={3}
                      value={employmentForm.description}
                      onChange={(e) => setEmploymentForm({
                        ...employmentForm,
                        description: e.target.value
                      })}
                      placeholder="Describe your key responsibilities, achievements, and duties in this role"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: 'white',
                          },
                        }
                      }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            {/* Employment Period Section */}
            <Box sx={{
              p: 3,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="h6" gutterBottom sx={{
                mb: 3,
                color: 'primary.main',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <CalendarIcon fontSize="small" />
                Employment Period
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      Start Date *
                    </Typography>
                    <MuiTextField
                      type="date"
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                      value={employmentForm.startDate}
                      onChange={(e) => setEmploymentForm({
                        ...employmentForm,
                        startDate: e.target.value
                      })}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: 'white',
                          },
                        }
                      }}
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      End Date
                    </Typography>
                    <MuiTextField
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={employmentForm.endDate}
                      onChange={(e) => setEmploymentForm({
                        ...employmentForm,
                        endDate: e.target.value
                      })}
                      disabled={employmentForm.current}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: employmentForm.current ? 'grey.100' : 'white',
                          '&:hover': {
                            backgroundColor: employmentForm.current ? 'grey.100' : 'white',
                          },
                        }
                      }}
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={employmentForm.current}
                        onChange={(e) => setEmploymentForm({
                          ...employmentForm,
                          current: e.target.checked,
                          endDate: e.target.checked ? '' : employmentForm.endDate
                        })}
                        sx={{
                          color: 'primary.main',
                          '&.Mui-checked': {
                            color: 'primary.main',
                          }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight={500}>
                        I currently work here
                      </Typography>
                    }
                    sx={{ mt: 1 }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Company Address Section */}
            <Box sx={{
              p: 3,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="h6" gutterBottom sx={{
                mb: 3,
                color: 'primary.main',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <LocationIcon fontSize="small" />
                Company Address
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      Country
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={employmentForm.countryCode}
                        onChange={(e) => setEmploymentForm({
                          ...employmentForm,
                          countryCode: e.target.value,
                          regionCode: '' // Clear province/state when country changes
                        })}
                        sx={{
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: 'white',
                          },
                        }}
                      >
                        <MenuItem value="CA">🇨🇦 Canada</MenuItem>
                        <MenuItem value="US">🇺🇸 United States</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      Address Line 1
                    </Typography>
                    <MuiTextField
                      fullWidth
                      value={employmentForm.address1}
                      onChange={(e) => setEmploymentForm({
                        ...employmentForm,
                        address1: e.target.value
                      })}
                      placeholder="Street address"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: 'white',
                          },
                        }
                      }}
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      Address Line 2
                    </Typography>
                    <MuiTextField
                      fullWidth
                      value={employmentForm.address2}
                      onChange={(e) => setEmploymentForm({
                        ...employmentForm,
                        address2: e.target.value
                      })}
                      placeholder="Apartment, suite, etc. (optional)"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: 'white',
                          },
                        }
                      }}
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      City
                    </Typography>
                    <MuiTextField
                      fullWidth
                      value={employmentForm.city}
                      onChange={(e) => setEmploymentForm({
                        ...employmentForm,
                        city: e.target.value
                      })}
                      placeholder="City"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: 'white',
                          },
                        }
                      }}
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      {employmentForm.countryCode === 'CA' ? 'Province' : 'State'}
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={employmentForm.regionCode}
                        onChange={(e) => setEmploymentForm({
                          ...employmentForm,
                          regionCode: e.target.value
                        })}
                        displayEmpty
                        sx={{
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: 'white',
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>Select {employmentForm.countryCode === 'CA' ? 'Province' : 'State'}</em>
                        </MenuItem>
                        {getProvinceStateOptions().map((option) => (
                          <MenuItem key={option.code} value={option.code}>
                            {option.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      Postal/Zip Code
                    </Typography>
                    <MuiTextField
                      fullWidth
                      value={employmentForm.postalCode}
                      onChange={(e) => setEmploymentForm({
                        ...employmentForm,
                        postalCode: e.target.value
                      })}
                      placeholder="Postal or Zip code"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: 'white',
                          },
                        }
                      }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            {/* Primary Contact Section */}
            <Box sx={{
              p: 3,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="h6" gutterBottom sx={{
                mb: 3,
                color: 'primary.main',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <PersonIcon fontSize="small" />
                Primary Contact (Optional)
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      Contact Name
                    </Typography>
                    <MuiTextField
                      fullWidth
                      value={employmentForm.contactName}
                      onChange={(e) => setEmploymentForm({
                        ...employmentForm,
                        contactName: e.target.value
                      })}
                      placeholder="Contact person name"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: 'white',
                          },
                        }
                      }}
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      Contact Title
                    </Typography>
                    <MuiTextField
                      fullWidth
                      value={employmentForm.contactTitle}
                      onChange={(e) => setEmploymentForm({
                        ...employmentForm,
                        contactTitle: e.target.value
                      })}
                      placeholder="Job title"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: 'white',
                          },
                        }
                      }}
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      Email
                    </Typography>
                    <MuiTextField
                      fullWidth
                      type="email"
                      value={employmentForm.contactEmail}
                      onChange={(e) => setEmploymentForm({
                        ...employmentForm,
                        contactEmail: e.target.value
                      })}
                      placeholder="email@company.com"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: 'white',
                          },
                        }
                      }}
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={8}>
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      Phone
                    </Typography>
                    <MuiTextField
                      fullWidth
                      value={employmentForm.contactPhone}
                      onChange={(e) => setEmploymentForm({
                        ...employmentForm,
                        contactPhone: e.target.value
                      })}
                      placeholder="Phone number"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: 'white',
                          },
                        }
                      }}
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      Extension
                    </Typography>
                    <MuiTextField
                      fullWidth
                      value={employmentForm.contactPhoneExt}
                      onChange={(e) => setEmploymentForm({
                        ...employmentForm,
                        contactPhoneExt: e.target.value
                      })}
                      placeholder="Ext."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover': {
                            backgroundColor: 'white',
                          },
                        }
                      }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{
          px: 4,
          py: 3,
          borderTop: '1px solid #e0e0e0',
          bgcolor: 'grey.50'
        }}>
          <Button
            onClick={() => setEmploymentDialogOpen(false)}
            variant="outlined"
            size="large"
            sx={{
              minWidth: 120,
              borderColor: 'grey.400',
              color: 'text.secondary'
            }}
          >
            Cancel
          </Button>

          {editingEmployment && (
            <Button
              onClick={() => {
                handleDeleteEmployment(editingEmployment.id);
                setEmploymentDialogOpen(false);
                setEditingEmployment(null);
              }}
              color="error"
              variant="outlined"
              size="large"
              startIcon={<DeleteIcon />}
              sx={{ minWidth: 120 }}
            >
              Delete
            </Button>
          )}

          <Button
            onClick={handleSaveEmployment}
            variant="contained"
            size="large"
            startIcon={<SaveIcon />}
            sx={{
              minWidth: 120,
              fontWeight: 600
            }}
          >
            {editingEmployment ? 'Update Employment' : 'Save Employment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};